import { ViewLayoutModel } from '../view/models/view-layout.model';
import { initialViewState } from '../view/state/view.state';
import { UiIO } from '../widget-api/models/ui.io';
import { UIConfig as URIUIConfig, WidgetConfig } from '../widget-config/initial-config';
import { ViewLayout } from '../view/models/view-layout.enum';
import { ScrollModeEnum } from '../engine/models/scroll-mode.enum';
import { LoaderTypeEnum } from '../view/models/loader-type.enum';
const layoutDefaults = initialViewState.layout;

// Dear future maintainers,
// https://docs.google.com/spreadsheets/d/1SXisKVARVtagG2OKWHzYoqKli6rSs89Y2J020smZkhc/edit?pli=1#gid=1968936664

interface UIConfig {
  [ViewLayout.Column]: boolean | undefined;
  [ViewLayout.Drawer]: boolean | undefined;
  [ViewLayout.Tools]: boolean | undefined;
  [ViewLayout.Toolbar]: boolean | undefined;
  [ViewLayout.Media]: boolean | undefined;
  [ViewLayout.Fullscreen]: boolean | undefined;
  [ViewLayout.Help]: boolean | undefined;
  [ViewLayout.Camera]: boolean | undefined;
  [ViewLayout.Search]: boolean | undefined;
  [ViewLayout.Presenter]: boolean | undefined;
  [ViewLayout.Menu]: boolean | undefined;
  [ViewLayout.Logo]: boolean | undefined;
  [ViewLayout.Audio]: boolean | undefined;
  [ViewLayout.Pagination]: boolean | undefined;
  [ViewLayout.NodeInfo]: boolean | undefined;
  [ViewLayout.Reset]: boolean | undefined;
  [ViewLayout.LabelList]: boolean | undefined;
  [ViewLayout.ChapterList]: boolean | undefined;
  'Layout.All': boolean | undefined;
  'Layout.LogoIsHyperLink': boolean | undefined;
  'Layout.DrawerExpandedInitially': boolean | undefined;
}

const setConfig = (_config: boolean, _default: boolean) =>
  _config === undefined ? _default : _config;


function valueForAliases(...aliases: Array<boolean | undefined>) {
  // are any of the aliases true?
  if (aliases.some(alias => alias === true)) {
    return true;
  }

  // are any of the aliases false?
  if (aliases.some(alias => alias === false)) {
    return false;
  }

  // let downstream decide.
  return undefined;
}


function getLayoutForUIAll(enable: boolean): ViewLayoutModel {
  return Object.keys(layoutDefaults).reduce(
    (acc: ViewLayoutModel, curr) => {
      if (curr === 'logo') { return acc; }
      acc[curr].enable = enable;
      return acc;
    },
    { ...layoutDefaults }
  );
}

export namespace WidgetWiring {

  // need to map UiIO.UIDisplayParams and URIUIConfig to UIConfig
  export function convertAPIUIConfigToUIConfig(apiConfig: UiIO.UIDisplayParams): UIConfig {

    // todo, split the return into a logical branch based on UiIO.UIDisplayParams.
    return {
      'Layout.All': apiConfig['all'],
      [ViewLayout.Help]: valueForAliases(
        apiConfig['help'],
        (apiConfig as UiIO.UIDisplayParamsInternal).help
      ),
      [ViewLayout.Tools]: valueForAliases(
        apiConfig['tools'],
        (apiConfig as UiIO.UIDisplayParamsInternal).tools
      ),
      [ViewLayout.Toolbar]: apiConfig['tools'],
      [ViewLayout.Fullscreen]: valueForAliases(
        apiConfig['fullscreen'],
        (apiConfig as UiIO.UIDisplayParamsInternal).fullscreen
      ),
      [ViewLayout.Search]: valueForAliases(
        apiConfig['objectTree'],
        (apiConfig as UiIO.UIDisplayParamsInternal).search
      ),
      [ViewLayout.Audio]: apiConfig['audio'],
      [ViewLayout.Media]: valueForAliases(
        apiConfig['playPause'],
        apiConfig['scrubber'],
        apiConfig['scrubber.chapters'],
        (apiConfig as UiIO.UIDisplayParamsInternal).media
      ),
      [ViewLayout.Camera]: valueForAliases(
        apiConfig['nav'],
        apiConfig['cameraMode'],
        apiConfig['center'],
        apiConfig['zoomIn'],
        apiConfig['zoomOut'],
        (apiConfig as UiIO.UIDisplayParamsInternal).nav
      ),
      [ViewLayout.Pagination]: valueForAliases(
        apiConfig['tour'],
        apiConfig['tour.chapters']
      ),

      [ViewLayout.Reset]: (apiConfig as UiIO.UIDisplayParamsInternal).reset,
      [ViewLayout.Column]: valueForAliases(
        apiConfig['info'],
        (apiConfig as UiIO.UIDisplayParamsInternal).info
      ),
      [ViewLayout.Drawer]: valueForAliases(
        apiConfig['info'],
        (apiConfig as UiIO.UIDisplayParamsInternal).info
      ),

      [ViewLayout.LabelList]: (apiConfig as UiIO.UIDisplayParamsInternal).labelList,
      [ViewLayout.ChapterList]: (apiConfig as UiIO.UIDisplayParamsInternal).chapterList,
      [ViewLayout.NodeInfo]: (apiConfig as UiIO.UIDisplayParamsInternal).anatomyDescriptions,


      [ViewLayout.Menu]: (apiConfig as UiIO.UIDisplayParamsInternal).menu,

      // not controlled by API
      [ViewLayout.Logo]: undefined,
      [ViewLayout.Presenter]: undefined,
      'Layout.LogoIsHyperLink': undefined,
      'Layout.DrawerExpandedInitially': undefined,
    };
  }


  export function convertURIUIConfigToUIConfig(uriConfig: URIUIConfig): UIConfig {
    return {
      [ViewLayout.NodeInfo]: uriConfig['ui-anatomy-descriptions'],
      [ViewLayout.Tools]: uriConfig['ui-tools'],
      [ViewLayout.Toolbar]: uriConfig['ui-tools'],
      [ViewLayout.Media]: valueForAliases(
        uriConfig['ui-media'],
        uriConfig['ui-scrubber'],
        uriConfig['ui-scrubber.chapters'],
        uriConfig['ui-scrubber.tour']
      ),
      [ViewLayout.Reset]: uriConfig['ui-reset'],
      [ViewLayout.Audio]: uriConfig['ui-audio'],
      [ViewLayout.Fullscreen]: uriConfig['ui-fullscreen'],
      [ViewLayout.Help]: uriConfig['ui-help'],
      [ViewLayout.Camera]: uriConfig['ui-nav'],
      [ViewLayout.Presenter]: uriConfig['ui-presenter'],
      [ViewLayout.Menu]: uriConfig['ui-menu'],
      [ViewLayout.Pagination]: uriConfig['ui-tour'],
      // handle params with aliases
      [ViewLayout.Search]: valueForAliases(
        uriConfig['ui-search'],
        uriConfig['ui-object-tree']
      ),
      [ViewLayout.Column]: valueForAliases(
        uriConfig['ui-info'],
        uriConfig['ui-panel']
      ),
      [ViewLayout.Drawer]: valueForAliases(
        uriConfig['ui-info'],
        uriConfig['ui-panel']
      ),
      [ViewLayout.Logo]: valueForAliases(uriConfig['ui-bd'], uriConfig['ui-logo']),
      'Layout.LogoIsHyperLink': valueForAliases(
        uriConfig['ui-bd.link'],
        uriConfig['ui-logo.islink']
      ),
      [ViewLayout.LabelList]: uriConfig['ui-label-list'],
      [ViewLayout.ChapterList]: uriConfig['ui-chapter-list'],
      'Layout.All': uriConfig['ui-all'],
      'Layout.DrawerExpandedInitially': uriConfig['ui-info-expand']
    };

  }

  export function createLayoutFromConfig(
    currentLayout: ViewLayoutModel,
    config: UIConfig
  ): ViewLayoutModel {
    const newLayout: ViewLayoutModel = JSON.parse(JSON.stringify(currentLayout));


    // respect 'all' param.
    // effect all layout except the logo
    if (config['Layout.All'] !== undefined) {
      return getLayoutForUIAll(config['Layout.All']);
    }


    newLayout.nodeInfo.enable = setConfig(
      config[ViewLayout.NodeInfo],
      layoutDefaults.nodeInfo.enable
    );

    newLayout.pagination.enable = setConfig(
      config[ViewLayout.Pagination],
      layoutDefaults.pagination.enable
    );

    newLayout.drawer.isExpandedInitially = setConfig(
      config['Layout.DrawerExpandedInitially'],
      layoutDefaults.drawer.isExpandedInitially
    );

    newLayout.tools.enable = setConfig(
      config[ViewLayout.Tools],
      layoutDefaults.tools.enable
    );

    newLayout.toolbar.enable = setConfig(
      config[ViewLayout.Tools],
      layoutDefaults.toolbar.enable
    );

    newLayout.media.enable = setConfig(
      config[ViewLayout.Media],
      layoutDefaults.media.enable
    );

    newLayout.fullscreen.enable = setConfig(
      config[ViewLayout.Fullscreen],
      layoutDefaults.fullscreen.enable
    );

    newLayout.help.enable = setConfig(
      config[ViewLayout.Help],
      layoutDefaults.help.enable
    );

    newLayout.description.enable = setConfig(
      config[ViewLayout.Description],
      layoutDefaults.description.enable
    );

    newLayout.camera.enable = setConfig(
      config[ViewLayout.Camera],
      layoutDefaults.camera.enable
    );

    newLayout.search.enable = setConfig(
      config[ViewLayout.Search],
      layoutDefaults.search.enable
    );

    newLayout.presenter.enable = setConfig(
      config[ViewLayout.Presenter],
      layoutDefaults.presenter.enable
    );

    newLayout.menu.enable = setConfig(
      config[ViewLayout.Menu],
      layoutDefaults.menu.enable
    );

    newLayout.logo.enable = setConfig(
      config[ViewLayout.Logo],
      layoutDefaults.logo.enable
    );

    newLayout.logo.isHyperLink = setConfig(
      config['Layout.LogoIsHyperLink'],
      layoutDefaults.logo.isHyperLink
    );

    newLayout.labelList.enable = setConfig(config[ViewLayout.LabelList], layoutDefaults.labelList.enable);
    newLayout.chapterList.enable = setConfig(config[ViewLayout.ChapterList], layoutDefaults.chapterList.enable);

    newLayout.audio.enable = setConfig(config[ViewLayout.Audio], layoutDefaults.audio.enable);
    newLayout.reset.enable = setConfig(config[ViewLayout.Reset], layoutDefaults.reset.enable);

    newLayout.column.enable = setConfig(config[ViewLayout.Column], layoutDefaults.column.enable);
    newLayout.drawer.enable = setConfig(config[ViewLayout.Drawer], layoutDefaults.drawer.enable);

    return newLayout;
  }


  export function getScrollSetting(widgetConfig: WidgetConfig) {
    if (widgetConfig['disable-scroll']) {
      return ScrollModeEnum.DoNotCapture;
    }

    if (widgetConfig['disable-scroll.alert'] || widgetConfig['disable-scroll.overlay']) {
      return ScrollModeEnum.DoNotCaptureShowMessage;
    }

    return ScrollModeEnum.Capture;
  }

  export function getUsePrintImage(widgetConfig: WidgetConfig) {
    return widgetConfig['ui-print-image'] === true;
  }


  export function getLoader(widgetConfig: WidgetConfig, currentLoaderActive: boolean, currentLoaderType: LoaderTypeEnum) {
    const isLoaderActive =
      widgetConfig['initial.play'] ||
      widgetConfig['initial'] ||
      widgetConfig['initial.button'] ||
      widgetConfig['initial.hand'] ||
      widgetConfig['initial.hand-hint'] ||
      currentLoaderActive;

    const loaderType = (() => {
      switch (true) {
        case widgetConfig['initial']:
        case widgetConfig['initial.button']:
          return LoaderTypeEnum.Button;

        case widgetConfig['initial.hand']:
        case widgetConfig['initial.hand-hint']:
          return LoaderTypeEnum.Hand;

        case widgetConfig['initial.play']:
          return LoaderTypeEnum.Play;

        default:
          return currentLoaderType;
      }
    })();

    return { loaderType, isLoaderActive };
  }
}
